import type {
  TableCombinationSchema,
  TableCombinationSchemaWithId,
  TableSchema,
  TableSchemaWithId,
} from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Table, TableCombination } from "~/providers/table-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { useTableStore } from "~/stores/use-table-store";
import { usePermissionManager } from "./use-permission-manager";

export const useTableManager = () => {
  const { t } = useTranslation("common");
  const { handleTableUpdate } = useTableUpdates();

  const {
    setTableContextOpen,
    setTableDialogOpen,
    setCurrentTable,
    setTableCombinationDialogOpen,
    setCurrentTableCombination,
  } = useTableStore();

  const utils = api.useUtils();

  const { mutate: saveLayout } = api.table.saveLayout.useMutation({
    onSuccess() {
      toast.success(capitalize(t("Table.layout_saved")));
    },
  });

  const { mutate: createTable } = api.table.createTable.useMutation({
    async onSuccess(result) {
      toast.success(
        capitalize(t("create_item_success", { item: t("Table.table") })),
      );
      await handleTableUpdate(result);
    },
  });

  const { mutate: updateTable } = api.table.updateTable.useMutation({
    async onSuccess(_, variables) {
      toast.success(
        capitalize(t("update_item_success", { item: t("Table.table") })),
      );
      await handleTableUpdate(variables.id);
    },
    onError() {
      toast.error(
        capitalize(t("update_item_error", { item: t("Table.table") })),
      );
    },
  });

  const { mutate: deleteTableCombinationMutation } =
    api.table.deleteTableCombination.useMutation({
      async onSuccess() {
        toast.success(
          capitalize(
            t("delete_item_success", { item: t("TableCombinations.title") }),
          ),
        );
        await utils.table.getTableCombinations.invalidate();
      },
      onError() {
        toast.error(
          capitalize(
            t("delete_item_fail", { item: t("TableCombinations.title") }),
          ),
        );
      },
    });

  const { mutate: createTableCominationMutation } =
    api.table.createTableCombination.useMutation({
      async onSuccess() {
        toast.success(
          capitalize(
            t("create_item_success", {
              item: t("TableCombinations.title"),
            }),
          ),
        );
        await utils.table.getTableCombinations.invalidate();
      },
      onError() {
        toast.error(
          capitalize(
            t("create_item_error", {
              item: t("TableCombinations.title"),
            }),
          ),
        );
      },
    });

  const { mutate: updateTableCombinationMutation } =
    api.table.updateTableCombination.useMutation({
      async onSuccess() {
        toast.success(
          capitalize(
            t("update_item_success", {
              item: t("TableCombinations.title"),
            }),
          ),
        );
        await utils.table.getTableCombinations.invalidate();
      },
      onError() {
        toast.error(
          capitalize(
            t("update_item_error", {
              item: t("TableCombinations.title"),
            }),
          ),
        );
      },
    });

  const { hasPermission } = usePermissionManager();

  const openTableContext = (table: Table) => {
    setTableContextOpen(table);
  };

  const openTableDialog = async (table?: Table) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_table,
      label: t("Permissions.create_table_label"),
    });
    if (!permission) return;
    setTableDialogOpen(true);
    setCurrentTable(table);
  };

  const openTableCombinationDialog = async (
    tableCombination?: TableCombination,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_table,
      label: t("Permissions.create_table_label"),
    });
    if (!permission) return;
    setTableCombinationDialogOpen(true);
    setCurrentTableCombination(tableCombination);
  };

  const updateTableHandler = async (
    table: z.infer<typeof TableSchemaWithId>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_table,
      label: t("Permissions.update_table_label"),
    });
    if (!permission) return;
    return await new Promise<boolean>((resolve) => {
      updateTable(table, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const createTableHanlder = async (table: z.infer<typeof TableSchema>) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_table,
      label: t("Permissions.create_table_label"),
    });

    return new Promise<ManagerResult<keyof z.infer<typeof TableSchema>>>(
      (resolve) => {
        if (!permission) {
          return resolve({ data: false });
        }
        createTable(table, {
          onSuccess() {
            toast.success(
              capitalize(t("create_item_success", { item: t("Table.table") })),
            );
            return resolve({
              data: true,
            });
          },
          onError(e) {
            if (e.data?.code === "CONFLICT") {
              if (e.message.includes("position")) {
                toast.error(capitalize(t("Table.position_taken")), {
                  duration: 5000,
                });
                return resolve({
                  error: {
                    path: "position",
                    message: t("Table.position_taken"),
                  },
                });
              } else if (e.message.includes("reference")) {
                toast.error(capitalize(t("Table.reference_taken")), {
                  duration: 5000,
                });
                return resolve({
                  error: {
                    path: "reference",
                    message: t("Table.reference_taken"),
                  },
                });
              }
            } else {
              toast.error(
                capitalize(t("create_item_error", { item: t("Table.table") })),
              );
            }

            return resolve({
              error: { path: "reference", message: t("Table.reference_taken") },
              data: undefined,
            });
          },
        });
      },
    );
  };

  const createTableCombination = async (
    tableCombination: z.infer<ReturnType<typeof TableCombinationSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_table,
      label: t("Permissions.create_table_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof TableCombinationSchema>>>
    >((resolve) => {
      if (!permission) {
        return resolve({ data: false });
      }
      createTableCominationMutation(tableCombination, {
        onSuccess() {
          resolve({ data: true });
        },
        onError() {
          resolve({ data: true });
        },
      });
    });
  };

  const updateTableCombination = async (
    tableCombination: z.infer<ReturnType<typeof TableCombinationSchemaWithId>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_table,
      label: t("Permissions.update_table_label"),
    });

    return new Promise<
      ManagerResult<
        keyof z.infer<ReturnType<typeof TableCombinationSchemaWithId>>
      >
    >((resolve) => {
      if (!permission) {
        return resolve({ data: false });
      }
      updateTableCombinationMutation(tableCombination, {
        onSuccess() {
          resolve({ data: true });
        },
        onError() {
          resolve({ data: true });
        },
      });
    });
  };

  const deleteTableCombination = async (tableCombinationId: string) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.delete_table,
    });

    return new Promise<boolean>((resolve) => {
      if (!permission) {
        return resolve(false);
      }
      deleteTableCombinationMutation(tableCombinationId, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };
  const saveLayoutHandler = (tables: Table[]) => {
    console.log(tables);
    saveLayout({
      items: tables.map((table) => ({
        id: table.id,
        reference: table.reference,
        rotation: table.rotation,
        position: table.position,
        locationId: table.locationId,
        posX: table.posX,
        posY: table.posY,
        width: table.width,
        height: table.height,
      })),
    });
  };

  return {
    openTableContext,
    createTableCombination,
    deleteTableCombination,
    updateTableCombination,
    openTableCombinationDialog,
    openTableDialog,
    updateTable: updateTableHandler,
    createTable: createTableHanlder,
    saveLayout: saveLayoutHandler,
  };
};

export const useTableUpdates = () => {
  const utils = api.useUtils();

  const handleTableUpdate = useCallback(
    async (tableId: string) => {
      const result = await utils.table.getById.fetch(tableId);

      void utils.table.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [utils.table.getAll, utils.table.getById],
  );

  const handleTableDelete = useCallback(
    (tableId: string) => {
      void utils.table.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== tableId);
      });
    },
    [utils.table.getAll],
  );

  return {
    handleTableUpdate,
    handleTableDelete,
  };
};
